// Briques communes aux écrans « Mes clés » et « Adresse d'alerte » : un seul appel ou geste à la fois, affichage des
// erreurs (navigateur ou API, toujours en français), relecture après écriture, motif avec confirmation explicite.
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ErreurApi } from '../api/client';
import { Erreur } from '../composants/communs';
import { ErreurGeste } from '../domaine/webauthn';

/** Un seul geste ou appel à la fois : occupé, erreur (navigateur ou API), succès. */
export function useAction() {
  const [occupe, setOccupe] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  async function executer<T>(nom: string, f: () => Promise<T>): Promise<T | undefined> {
    setErreur(null);
    setOccupe(nom);
    try {
      return await f();
    } catch (e) {
      setErreur(e);
      return undefined;
    } finally {
      setOccupe(null);
    }
  }
  return { occupe, erreur, executer, effacer: () => setErreur(null) };
}

export function MessageErreur({ erreur, titre }: { erreur: unknown; titre: string }) {
  if (!erreur) return null;
  if (erreur instanceof ErreurGeste)
    return (
      <p className="encadre attention" role="alert" data-testid="erreur-geste">
        {erreur.message}
      </p>
    );
  return (
    <>
      <Erreur erreur={erreur} titre={titre} />
      {erreur instanceof ErreurApi && erreur.code === 'adresse_alerte_invalide' && <LienAdresseAlerte />}
    </>
  );
}

/** Mène à la section « Adresse d'alerte » de l'écran « Mes clés » (même page : on fait défiler et on met le focus, sans changer l'adresse). */
export function LienAdresseAlerte() {
  return (
    <button
      type="button"
      className="btn petit"
      data-testid="vers-adresse-alerte"
      onClick={() => {
        const t = document.getElementById('t-adresse-alerte');
        t?.scrollIntoView?.({ block: 'start' });
        t?.focus();
      }}
    >
      Aller à « Adresse d’alerte »
    </button>
  );
}

/** Relit tout ce qui dépend des clés et des adresses après une écriture (l'état de la personne vient de GET /v0/moi). */
export function useRelireCles() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['moi'] }),
      qc.invalidateQueries({ queryKey: ['mes-cles'] }),
      qc.invalidateQueries({ queryKey: ['cles-client'] }),
      qc.invalidateQueries({ queryKey: ['adresses-client'] }),
    ]);
}

// ---------------------------------------------------------------- motif + confirmation

export function FormulaireMotif({
  libelle,
  bouton,
  occupe,
  surConfirmer,
  surAnnuler,
  aide,
  facultatif = false,
}: {
  libelle: string;
  bouton: string;
  occupe: boolean;
  surConfirmer: (motif: string) => void;
  surAnnuler: () => void;
  aide?: string;
  /** Motif non exigé (annulation par la personne elle-même). */
  facultatif?: boolean;
}) {
  const [motif, setMotif] = useState('');
  const id = useId();
  return (
    <div className="pile" style={{ gap: 6, marginTop: 8 }}>
      <div className="champ">
        <label htmlFor={id}>
          {libelle} <span className="aide">({facultatif ? 'facultatif' : 'obligatoire'})</span>
        </label>
        <textarea id={id} value={motif} maxLength={500} onChange={(e) => setMotif(e.target.value)} />
      </div>
      {aide && <p className="note">{aide}</p>}
      <div className="ligne">
        <button type="button" className="btn principal petit" disabled={occupe || (!facultatif && motif.trim().length === 0)} onClick={() => surConfirmer(motif.trim())}>
          {bouton}
        </button>
        <button type="button" className="btn petit" disabled={occupe} onClick={surAnnuler}>
          Annuler
        </button>
      </div>
    </div>
  );
}
